import { useEffect, useMemo, useRef, useState } from 'react'
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { serversApi } from '../../api'
import { getErrorMessage } from '../../api/client'
import type { DeviceSpecDevice, EdgeDeviceChannel } from '../../types'
import { ComparePreview, PlanBadge } from './CompareView'
import { planOf } from './specCompare'
import type { PlanState } from './specCompare'
import { mergeEdgeDevice } from './pullMerge'

// Edge → 명세 가져오기 — 서버를 고르면 그 Edge 에 실제로 설정된 디바이스가 나오고, 골라서 명세에 넣는다 (현장 우선).
// 같은 코드가 명세에 있으면 덮어쓰고(명세 전용 칸은 남김, pullMerge), 없으면 추가. 반영은 한 번에(되돌리기 한 번).
// 같은 코드인 디바이스는 채널 설정까지 미리 받아 비교한다 — 가져와도 그대로면 '변경 없음' 으로 두고 고르지 않는다.
// 비교·미리보기는 [명세 → Edge 적용] 과 같은 화면(CompareView). 읽기만 하므로 모니터링 중이어도 된다 — Edge 가 온라인이면 된다.

export default function PullFromEdgeDialog({ specDevices, onApply, onClose }: {
  specDevices: DeviceSpecDevice[]
  onApply: (devices: DeviceSpecDevice[], summary: string) => void
  onClose: () => void
}) {
  const [serverId, setServerId] = useState('')
  const [checked, setChecked] = useState<Set<string>>(new Set())
  const [previewCode, setPreviewCode] = useState<string | null>(null)
  const [keepSpecOnly, setKeepSpecOnly] = useState(true)
  const [running, setRunning] = useState(false)
  const qc = useQueryClient()
  const [error, setError] = useState<string | null>(null)

  const closeRef = useRef(onClose)
  useEffect(() => { closeRef.current = onClose })
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !running) closeRef.current() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [running])

  const serversQuery = useQuery({
    queryKey: ['servers'],
    queryFn: () => serversApi.getAll().then(r => r.data),
  })
  const servers = (serversQuery.data ?? []).filter(s => s.status === 'Registered')
  const devicesQuery = useQuery({
    queryKey: ['servers', serverId, 'devices'],
    queryFn: () => serversApi.getDevices(serverId).then(r => r.data),
    enabled: !!serverId,
    retry: false,
    staleTime: 0,
  })
  const edgeDevices = useMemo(() => devicesQuery.data ?? [], [devicesQuery.data])
  const specOf = (code: string) => specDevices.find(d => d.code.trim() === code)
  // 명세에 같은 코드가 있는 디바이스만 채널을 미리 받는다 (현장 규모 ≤20)
  const channelKey = (code: string) => ['servers', serverId, 'devices', code, 'channel']
  const matchedCodes = useMemo(() => edgeDevices.map(e => e.code).filter(c => specDevices.some(d => d.code.trim() === c)), [edgeDevices, specDevices])
  const channelQueries = useQueries({
    queries: matchedCodes.map(code => ({
      queryKey: channelKey(code),
      queryFn: () => serversApi.getDeviceChannel(serverId, code).then(r => r.data),
      retry: false,
      staleTime: 0,
    })),
  })
  const channels = new Map<string, EdgeDeviceChannel>()
  const channelFailed = new Set<string>()
  matchedCodes.forEach((code, i) => {
    const q = channelQueries[i]
    if (q?.data) channels.set(code, q.data)
    else if (q?.isError) channelFailed.add(code)
  })
  const plans: Record<string, PlanState> = Object.fromEntries(edgeDevices.map(e =>
    [e.code, planOf(specOf(e.code), e, channels.get(e.code), 'pull', keepSpecOnly, channelFailed.has(e.code))]))
  const selectable = edgeDevices.filter(e => plans[e.code]?.kind !== 'same')
  const selected = selectable.filter(e => checked.has(e.code))
  const removing = selected.reduce((n, e) => { const p = plans[e.code]; return n + (p?.kind === 'update' ? p.summary.remove : 0) }, 0)
  // 미리보기 — 누른 디바이스, 아직 안 눌렀으면 첫 번째로 바뀌는(또는 새로 추가되는) 디바이스
  const previewKey = previewCode ?? selectable[0]?.code ?? edgeDevices[0]?.code ?? null
  const preview = edgeDevices.find(e => e.code === previewKey) ?? null

  const pickServer = (id: string) => {
    setServerId(id)
    setChecked(new Set())
    setPreviewCode(null)
    setError(null)
  }

  const toggle = (code: string) => setChecked(s => {
    const next = new Set(s)
    if (next.has(code)) next.delete(code)
    else next.add(code)
    return next
  })

  // 채널 설정은 디바이스마다 따로 받아야 해서(목록에는 없음) 차례로 받은 뒤 한 번에 반영한다
  const apply = async () => {
    if (selected.length === 0 || running) return
    if (removing > 0 && !confirm(`명세의 태그 ${removing}개가 Edge 에 없어 명세에서 삭제됩니다. 가져올까요? (Ctrl+Z 로 되돌릴 수 있습니다)`)) return
    setRunning(true)
    setError(null)
    try {
      let devices = [...specDevices]
      let added = 0, updated = 0
      for (const e of selected) {
        const channel = await qc.fetchQuery({
          queryKey: channelKey(e.code),
          queryFn: () => serversApi.getDeviceChannel(serverId, e.code).then(r => r.data),
          staleTime: 60_000,
        })
        const existing = devices.find(d => d.code.trim() === e.code)
        const merged = mergeEdgeDevice(e, channel, existing, keepSpecOnly)
        if (existing) { devices = devices.map(d => (d.key === existing.key ? merged : d)); updated++ }
        else { devices = [...devices, merged]; added++ }
      }
      onApply(devices, `Edge 에서 가져왔습니다 (추가 ${added}, 덮어쓰기 ${updated})`)
    } catch (err) {
      setError(getErrorMessage(err, 'Edge 에서 설정을 받지 못했습니다.'))
      setRunning(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" onMouseDown={() => { if (!running) onClose() }}>
      <div className="bg-surface rounded-xl shadow-xl w-full max-w-6xl h-[85vh] flex flex-col overflow-hidden" onMouseDown={e => e.stopPropagation()}>
        <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-gray-100 shrink-0">
          <h3 className="font-semibold text-gray-800">Edge → 명세 가져오기</h3>
          <select value={serverId} onChange={e => pickServer(e.target.value)} disabled={running}
            className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm min-w-[280px]">
            <option value="">{serversQuery.isLoading ? '불러오는 중…' : '서버를 고르세요'}</option>
            {servers.map(s => (
              <option key={s.id} value={s.id} disabled={!s.isOnline}>
                {s.name}{s.company ? ` · ${s.company.name}` : ''}{s.isOnline ? '' : ' (오프라인)'}
              </option>
            ))}
          </select>
          {devicesQuery.isFetching && <span className="text-xs text-gray-400">Edge 에서 받는 중…</span>}
          <span className="ml-auto text-xs text-gray-500">Edge 값으로 명세를 맞춥니다 · 항목명·설명 등 명세 전용 칸은 그대로</span>
        </div>

        <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
          <div className="min-h-0 overflow-auto border-r border-gray-100">
            {!serverId ? (
              <p className="p-8 text-center text-sm text-gray-400">서버를 고르면 그 Edge 의 디바이스가 나옵니다.</p>
            ) : devicesQuery.isError ? (
              <p className="p-8 text-center text-sm text-red-600">{getErrorMessage(devicesQuery.error, '디바이스 목록을 받지 못했습니다.')}</p>
            ) : edgeDevices.length === 0 && !devicesQuery.isFetching ? (
              <p className="p-8 text-center text-sm text-gray-400">이 Edge 에는 디바이스가 없습니다.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs sticky top-0 z-10">
                  <tr>
                    <th className="w-10 px-3 py-2">
                      <input type="checkbox" disabled={running || selectable.length === 0} title="변경 없는 디바이스는 빼고 고릅니다"
                        checked={selectable.length > 0 && selectable.every(e => checked.has(e.code))}
                        onChange={ev => setChecked(ev.target.checked ? new Set(selectable.map(e => e.code)) : new Set())} />
                    </th>
                    <th className="text-left px-2 py-2">Edge 디바이스</th>
                    <th className="text-left px-2 py-2">프로토콜</th>
                    <th className="text-left px-2 py-2">가져오면 명세는</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {edgeDevices.map(e => (
                    <tr key={e.code} onClick={() => setPreviewCode(e.code)}
                      className={`cursor-pointer align-top ${e.code === previewKey ? 'bg-blue-50/60' : 'hover:bg-gray-50'} ${plans[e.code]?.kind === 'same' ? 'opacity-60' : ''}`}>
                      <td className="px-3 py-2.5" onClick={ev => ev.stopPropagation()}>
                        <input type="checkbox" checked={plans[e.code]?.kind !== 'same' && checked.has(e.code)}
                          disabled={running || plans[e.code]?.kind === 'same'} onChange={() => toggle(e.code)} />
                      </td>
                      <td className="px-2 py-2.5">
                        <div className="font-medium text-gray-800">{e.name}</div>
                        <div className="text-xs text-gray-500 font-mono">{e.code} · 태그 {e.tags.length}</div>
                      </td>
                      <td className="px-2 py-2.5 text-xs text-gray-600">{e.protocol || '-'}</td>
                      <td className="px-2 py-2.5 text-xs"><PlanBadge plan={plans[e.code]} dir="pull" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="min-h-0 overflow-auto">
            {preview
              ? <ComparePreview key={preview.code} dir="pull" spec={specOf(preview.code)} edge={preview} channel={channels.get(preview.code)} keepSpecOnly={keepSpecOnly} />
              : <p className="p-8 text-center text-sm text-gray-400">디바이스를 누르면 명세와 다른 곳을 볼 수 있습니다.</p>}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 px-5 py-3 border-t border-gray-100 shrink-0">
          <span className="text-xs text-gray-500">
            {selected.length > 0 ? `${selected.length}개 선택` : '가져올 디바이스를 고르세요'}
            {removing > 0 && <span className="text-red-700 font-semibold"> · 명세 태그 {removing}개 삭제</span>}
          </span>
          <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none"
            title="아직 Edge 에 넣지 않은 새 태그 등, Edge 에 없는 명세 태그를 지우지 않고 남깁니다">
            <input type="checkbox" checked={keepSpecOnly} disabled={running} onChange={ev => setKeepSpecOnly(ev.target.checked)} />
            명세에만 있는 태그는 남기기
          </label>
          {error && <span className="text-xs text-red-600">{error}</span>}
          <div className="ml-auto flex gap-2">
            <button onClick={onClose} disabled={running}
              className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50 transition disabled:opacity-50">취소</button>
            <button onClick={apply} disabled={running || selected.length === 0}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-accent-hover transition disabled:opacity-50">
              {running ? '가져오는 중…' : `명세에 반영${selected.length ? ` (${selected.length})` : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
